import React, { useState, useEffect, useCallback } from 'react';
import { branding } from '../constants/branding';
import { apiService, StablecoinConfigResponse } from '../services/apiService';
import { blockchainService } from '../services/blockchainService';

// ─── Types ───────────────────────────────────────────────────────────────────
interface NetworkGroup {
  network: string;
  icon: string;
  coins: StablecoinConfigResponse[];
  sendEnabled: boolean;
  receiveEnabled: boolean;
  convertEnabled: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────
const Stablecoin: React.FC = () => {
  const MOBILE_BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // Config from backend (admin-managed)
  const [configs, setConfigs] = useState<StablecoinConfigResponse[]>([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  // Derived: unique networks with their coins
  const [networkGroups, setNetworkGroups] = useState<NetworkGroup[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkGroup | null>(null);
  const [selectedCoin, setSelectedCoin]    = useState<StablecoinConfigResponse | null>(null);

  // Wallet state (from blockchain service / backend)
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [balances, setBalances]           = useState<Record<string, number>>({});
  const [balancesLoading, setBalancesLoading] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState<'send' | 'receive' | 'convert'>('send');

  // Form fields
  const [sendRecipient, setSendRecipient] = useState('');
  const [sendAmount, setSendAmount]       = useState('');
  const [convertAmount, setConvertAmount] = useState('');
  const [convertToCoin, setConvertToCoin] = useState('');

  // Status
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // ── Fetch enabled configs from backend ──────────────────────────────────
  useEffect(() => {
    const loadConfigs = async () => {
      setConfigLoading(true);
      setConfigError(null);
      try {
        const res = await apiService.getEnabledStablecoinConfigs();
        if (res.success && res.data) {
          setConfigs(res.data);
        } else {
          setConfigError(res.error || 'Failed to load stablecoin configurations');
        }
      } catch (err: any) {
        setConfigError(err.message || 'Failed to load stablecoin configurations');
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfigs();

    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Group configs by network ─────────────────────────────────────────────
  useEffect(() => {
    if (configs.length === 0) { setNetworkGroups([]); return; }

    const groupMap = new Map<string, NetworkGroup>();
    configs.forEach(c => {
      if (!groupMap.has(c.network)) {
        groupMap.set(c.network, {
          network: c.network,
          icon: c.icon || networkIcon(c.network),
          coins: [],
          sendEnabled:    false,
          receiveEnabled: false,
          convertEnabled: false,
        });
      }
      const g = groupMap.get(c.network)!;
      g.coins.push(c);
      if (c.sendEnabled)    g.sendEnabled    = true;
      if (c.receiveEnabled) g.receiveEnabled = true;
      if (c.convertEnabled) g.convertEnabled = true;
    });

    const groups = Array.from(groupMap.values());
    setNetworkGroups(groups);

    // Default select first network & first coin
    const first = groups[0];
    setSelectedNetwork(first);
    setSelectedCoin(first?.coins[0] || null);
    setConvertToCoin(first?.coins[0]?.symbol || '');
  }, [configs]);

  // ── Load balances when network changes ──────────────────────────────────
  const loadBalances = useCallback(async (group: NetworkGroup) => {
    setBalancesLoading(true);
    try {
      const address = await blockchainService.getWalletAddress(group.network.toLowerCase());
      setWalletAddress(address || 'N/A');

      const newBalances: Record<string, number> = {};
      for (const coin of group.coins) {
        const bal = await blockchainService.getBalance(group.network.toLowerCase(), coin.symbol);
        newBalances[coin.symbol] = bal || 0;
      }
      setBalances(newBalances);
    } catch {
      setWalletAddress('N/A');
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedNetwork) loadBalances(selectedNetwork);
  }, [selectedNetwork, loadBalances]);

  // Auto-clear messages
  useEffect(() => {
    if (actionSuccess) { const t = setTimeout(() => setActionSuccess(null), 4000); return () => clearTimeout(t); }
  }, [actionSuccess]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleNetworkChange = (networkName: string) => {
    const group = networkGroups.find(g => g.network === networkName) || null;
    setSelectedNetwork(group);
    setSelectedCoin(group?.coins[0] || null);
    setConvertToCoin(group?.coins[0]?.symbol || '');
    setActionError(null);
    setActionSuccess(null);
  };

  const handleSend = async () => {
    if (!sendRecipient.trim()) { setActionError('Please enter a recipient address'); return; }
    if (!sendAmount || isNaN(+sendAmount) || +sendAmount <= 0) { setActionError('Please enter a valid amount'); return; }
    if (!selectedCoin) return;

    // Validate limits
    if (selectedCoin.transactionLimit && +sendAmount > selectedCoin.transactionLimit) {
      setActionError(`Transaction limit is $${selectedCoin.transactionLimit.toLocaleString()}`);
      return;
    }
    if (selectedCoin.minimumAmount && +sendAmount < selectedCoin.minimumAmount) {
      setActionError(`Minimum transaction is $${selectedCoin.minimumAmount}`);
      return;
    }

    setActionLoading(true);
    setActionError(null);
    try {
      const res = await apiService.post('/stablecoin/send', {
        toWalletAddress: sendRecipient,
        amount:          parseFloat(sendAmount),
        symbol:          selectedCoin.symbol,
        network:         selectedCoin.network,
      });
      if (res.success) {
        setActionSuccess(`Sent ${sendAmount} ${selectedCoin.symbol} successfully!`);
        setSendRecipient('');
        setSendAmount('');
        loadBalances(selectedNetwork!);
      } else {
        setActionError(res.error || 'Failed to send');
      }
    } catch (err: any) {
      setActionError(err.message || 'Send failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!convertAmount || isNaN(+convertAmount) || +convertAmount <= 0) {
      setActionError('Please enter a valid amount');
      return;
    }
    if (!selectedCoin) return;

    setActionLoading(true);
    setActionError(null);
    try {
      const res = await apiService.post('/stablecoin/convert', {
        amount:  parseFloat(convertAmount),
        symbol:  convertToCoin || selectedCoin.symbol,
        network: selectedCoin.network,
        // fromAccountId is resolved server-side from the user's primary account
      });
      if (res.success) {
        setActionSuccess(`Converted ${convertAmount} to ${convertToCoin} successfully!`);
        setConvertAmount('');
        loadBalances(selectedNetwork!);
      } else {
        setActionError(res.error || 'Conversion failed');
      }
    } catch (err: any) {
      setActionError(err.message || 'Conversion failed');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Total balance across all coins on selected network ──────────────────
  const totalStablecoinValue = Object.values(balances).reduce((s, b) => s + b, 0);

  // ─────────────────────────────────────────────────────────────────────────
  // Loading / error states
  // ─────────────────────────────────────────────────────────────────────────
  if (configLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 36 }}>⏳</div>
        <p style={{ color: '#64748b' }}>Loading stablecoin configuration…</p>
      </div>
    );
  }

  if (configError) {
    return (
      <div style={{ padding: 32, maxWidth: 600, margin: '0 auto' }}>
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h3 style={{ color: '#991b1b', margin: '0 0 8px' }}>Failed to load stablecoin settings</h3>
          <p style={{ color: '#b91c1c', margin: 0 }}>{configError}</p>
        </div>
      </div>
    );
  }

  if (networkGroups.length === 0) {
    return (
      <div style={{ padding: 32, maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🚫</div>
        <h2 style={{ color: branding.textColor }}>Stablecoins Unavailable</h2>
        <p style={{ color: '#64748b' }}>No stablecoin services are currently enabled. Please contact your administrator.</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Main UI
  // ─────────────────────────────────────────────────────────────────────────
  const canSend    = selectedNetwork?.sendEnabled    && selectedCoin?.sendEnabled;
  const canReceive = selectedNetwork?.receiveEnabled && selectedCoin?.receiveEnabled;
  const canConvert = selectedNetwork?.convertEnabled && selectedCoin?.convertEnabled;

  return (
    <div style={{ fontFamily: branding.fontFamily, padding: isMobile ? '16px 16px 120px' : '32px', background: branding.pageBackground, minHeight: '100vh' }}>
      <div style={{ maxWidth: branding.viewport.mainMaxWidth, margin: '0 auto' }}>

        {/* ── Page Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ color: branding.textColor, fontSize: isMobile ? 22 : 28, margin: 0 }}>
              💱 Stablecoin Payments
            </h1>
            <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
              Send, receive, and convert stablecoins across multiple networks
            </p>
          </div>
          {/* Network Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>{selectedNetwork?.icon}</span>
            <select
              value={selectedNetwork?.network || ''}
              onChange={e => handleNetworkChange(e.target.value)}
              style={{ ...inputStyle, width: 'auto', padding: '8px 14px' }}
            >
              {networkGroups.map(g => (
                <option key={g.network} value={g.network}>{g.network}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Action feedback ── */}
        {actionError && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#991b1b', fontSize: 14 }}>
            ⚠️ {actionError}
          </div>
        )}
        {actionSuccess && (
          <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#065f46', fontSize: 14 }}>
            ✅ {actionSuccess}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 24 }}>

          {/* ── Left column (main) ── */}
          <div style={{ flex: 2 }}>

            {/* Balance card */}
            <div style={{ background: branding.primaryColor, color: '#fff', borderRadius: 20, padding: 24, marginBottom: 24 }}>
              <div style={{ opacity: 0.8, fontSize: 13 }}>Total Stablecoin Balance ({selectedNetwork?.network})</div>
              <div style={{ fontSize: 34, fontWeight: 700, margin: '8px 0' }}>
                {balancesLoading ? '…' : `$${totalStablecoinValue.toFixed(2)}`}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, opacity: 0.85 }}>
                {selectedNetwork?.coins.map((coin, idx) => (
                  <span key={coin.symbol}>
                    {coin.symbol}: {balancesLoading ? '…' : (balances[coin.symbol] || 0).toFixed(2)}
                    {idx < selectedNetwork.coins.length - 1 && ' ·'}
                  </span>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div style={cardStyle}>
              <div style={{ display: 'flex', borderBottom: '2px solid #f1f5f9', marginBottom: 24 }}>
                {canSend    && <TabButton label="📤 Send"    active={activeTab === 'send'}    onClick={() => setActiveTab('send')} />}
                {canReceive && <TabButton label="📥 Receive" active={activeTab === 'receive'} onClick={() => setActiveTab('receive')} />}
                {canConvert && <TabButton label="🔄 Convert" active={activeTab === 'convert'} onClick={() => setActiveTab('convert')} />}
                {!canSend && !canReceive && !canConvert && (
                  <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>No operations available for this stablecoin.</p>
                )}
              </div>

              {/* ── Send Tab ── */}
              {activeTab === 'send' && canSend && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 style={{ color: branding.textColor, margin: 0 }}>Send Stablecoins</h3>

                  <div>
                    <label style={labelStyle}>Recipient Address or Username</label>
                    <input type="text" placeholder="0x… or @username" value={sendRecipient}
                      onChange={e => setSendRecipient(e.target.value)} style={inputStyle} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Amount</label>
                      <input type="number" min={0} placeholder="0.00" value={sendAmount}
                        onChange={e => setSendAmount(e.target.value)} style={inputStyle} />
                      {selectedCoin?.transactionLimit && (
                        <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>
                          Max: ${selectedCoin.transactionLimit.toLocaleString()}
                        </p>
                      )}
                      {(selectedCoin?.minimumAmount ?? 0) > 0 && (
                        <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0' }}>
                          Min: ${selectedCoin!.minimumAmount}
                        </p>
                      )}
                    </div>
                    <div>
                      <label style={labelStyle}>Stablecoin</label>
                      <select value={selectedCoin?.symbol || ''}
                        onChange={e => setSelectedCoin(selectedNetwork?.coins.find(c => c.symbol === e.target.value) || null)}
                        style={inputStyle}>
                        {selectedNetwork?.coins.filter(c => c.sendEnabled).map(c => (
                          <option key={c.symbol} value={c.symbol}>{c.symbol} — {c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {selectedCoin?.dailyLimit && (
                    <p style={{ fontSize: 12, color: '#f59e0b', margin: 0 }}>
                      ⚠️ Daily limit: ${selectedCoin.dailyLimit.toLocaleString()} / day
                    </p>
                  )}

                  <button style={{ ...buttonStyle, opacity: actionLoading ? 0.6 : 1 }}
                    disabled={actionLoading} onClick={handleSend}>
                    {actionLoading ? 'Processing…' : '📤 Send Payment'}
                  </button>
                </div>
              )}

              {/* ── Receive Tab ── */}
              {activeTab === 'receive' && canReceive && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 style={{ color: branding.textColor, margin: 0 }}>Receive Stablecoins</h3>
                  <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <p style={{ color: '#64748b', fontSize: 13 }}>Your {selectedNetwork?.network} Wallet Address:</p>
                    <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: 10, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', border: '1px solid #e2e8f0', marginBottom: 16 }}>
                      {walletAddress}
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(walletAddress); setActionSuccess('Address copied!'); }}
                      style={{ ...buttonStyle, width: 'auto', padding: '8px 20px', fontSize: 13 }}>
                      📋 Copy Address
                    </button>
                    <div style={{ width: 150, height: 150, background: '#e2e8f0', margin: '20px auto', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, color: '#94a3b8', fontSize: 13 }}>
                      QR Code
                    </div>
                    <p style={{ color: '#64748b', fontSize: 12 }}>
                      Send only {selectedNetwork?.coins.map(c => c.symbol).join(', ')} to this address on {selectedNetwork?.network}.
                    </p>
                  </div>
                </div>
              )}

              {/* ── Convert Tab ── */}
              {activeTab === 'convert' && canConvert && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 style={{ color: branding.textColor, margin: 0 }}>Convert Funds</h3>

                  <div>
                    <label style={labelStyle}>Amount to Convert</label>
                    <input type="number" min={0} placeholder="0.00" value={convertAmount}
                      onChange={e => setConvertAmount(e.target.value)} style={inputStyle} />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>From</label>
                      <select style={inputStyle}>
                        <option value="main">Main Account (Fiat)</option>
                      </select>
                    </div>
                    <span style={{ fontSize: 22, marginTop: 20, color: '#64748b' }}>→</span>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>To Stablecoin</label>
                      <select value={convertToCoin} onChange={e => setConvertToCoin(e.target.value)} style={inputStyle}>
                        {selectedNetwork?.coins.filter(c => c.convertEnabled).map(c => (
                          <option key={c.symbol} value={c.symbol}>{c.symbol} — {c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
                    Exchange Rate: 1 USD ≈ 1.00 {convertToCoin}
                  </p>

                  <button style={{ ...buttonStyle, opacity: actionLoading ? 0.6 : 1 }}
                    disabled={actionLoading} onClick={handleConvert}>
                    {actionLoading ? 'Processing…' : '🔄 Convert'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Right column (info) ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Available coins on this network */}
            <div style={cardStyle}>
              <h3 style={{ color: branding.textColor, marginTop: 0, marginBottom: 16, fontSize: 15 }}>
                Available on {selectedNetwork?.network}
              </h3>
              {selectedNetwork?.coins.map(coin => (
                <div key={coin.symbol} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{coin.symbol}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{coin.name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {balancesLoading ? '…' : `$${(balances[coin.symbol] || 0).toFixed(2)}`}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>Balance</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Coin limits info */}
            {selectedCoin && (selectedCoin.transactionLimit || selectedCoin.dailyLimit || (selectedCoin.minimumAmount ?? 0) > 0) && (
              <div style={cardStyle}>
                <h3 style={{ color: branding.textColor, marginTop: 0, marginBottom: 12, fontSize: 15 }}>
                  💰 Limits for {selectedCoin.symbol}
                </h3>
                <div style={{ fontSize: 13, color: '#374151', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(selectedCoin.minimumAmount ?? 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>Minimum</span>
                      <span style={{ fontWeight: 600 }}>${selectedCoin.minimumAmount}</span>
                    </div>
                  )}
                  {selectedCoin.transactionLimit && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>Per Transaction</span>
                      <span style={{ fontWeight: 600 }}>${selectedCoin.transactionLimit.toLocaleString()}</span>
                    </div>
                  )}
                  {selectedCoin.dailyLimit && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>Daily</span>
                      <span style={{ fontWeight: 600 }}>${selectedCoin.dailyLimit.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Coin description */}
            {selectedCoin?.description && (
              <div style={cardStyle}>
                <h3 style={{ color: branding.textColor, marginTop: 0, marginBottom: 8, fontSize: 15 }}>ℹ️ About {selectedCoin.symbol}</h3>
                <p style={{ color: '#64748b', fontSize: 13, margin: 0, lineHeight: 1.5 }}>{selectedCoin.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────
const TabButton: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: '10px 18px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: active ? 700 : 400,
    color: active ? branding.primaryColor : '#64748b',
    borderBottom: active ? `3px solid ${branding.primaryColor}` : '3px solid transparent',
    marginBottom: -2,
    transition: 'all 0.15s',
  }}>
    {label}
  </button>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const networkIcon = (network: string): string => {
  const icons: Record<string, string> = {
    ETHEREUM: 'Ξ',
    SOLANA: 'S',
    POLYGON: 'P',
    STELLAR: '✦',
    CARDANO: '₳',
    BITCOIN: '₿',
  };
  return icons[network.toUpperCase()] || '🔗';
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 20,
  padding: 24,
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 10,
  border: '1.5px solid #e2e8f0',
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const buttonStyle: React.CSSProperties = {
  width: '100%',
  padding: '13px 16px',
  borderRadius: 10,
  border: 'none',
  background: branding.primaryColor,
  color: '#fff',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};

export default Stablecoin;
